// =============================================================================
// DEVIS & FACTURES - Liste des devis avec brouillons
// Migré vers OHGrid v1.1.1 (GROOT UI-002/004/005/006/007)
// =============================================================================
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FileText, Clock, CheckCircle2, AlertTriangle, Eye, Edit, Trash2, Lock, Send,
  Receipt, RefreshCw, Calendar, Euro, User, Unlock, FileCheck, Banknote,
  Archive, ArchiveRestore, Download,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
// 🔑 LA garde canonique des capabilities — la MÊME que le menu (OmniHub.jsx) et
// que `EnvoyerVisiteBlock`. ⛔ Aucun slug, ⛔ aucun second mécanisme.
import { usePermissions } from "../../contexts/PermissionContext";
import { OHGrid } from "../../components/ui/oh-grid";
import { emettreDevis } from "./artisan/api";
import { isInstallateurProfile, getBusinessModel } from "../../utils/tenantProfile";
import { TRI_ETAT } from "../../lib/filtreTriEtat";
import { chargerTousLesDevis } from "../../lib/chargerTousLesDevis";
import SendDevisOmvModal from "../../components/omv/SendDevisOmvModal";
import DevisEnvoiTrace from "../../components/omv/DevisEnvoiTrace";
import { withDevisPrefix, devisPrefixEnabled } from "../../utils/devisDisplay";
import { devisStatutKey, devisStatutLabel, DEVIS_STATUT_DEFAULTS, estModifiableDevis } from "../../utils/devisStatut";
import { useConfigList } from "../../hooks/useConfigList";
import { enteteTenant } from "../../utils/enteteTenant";
import ChampDate from "../../components/ui/champ-date";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Mapping des statuts avec niveaux de verrouillage
const STATUT_CONFIG = {
  draft: { label: "Brouillon", color: "bg-zinc-500/20 border border-[var(--oh-border)]", icon: Edit, verrouille: false },
  validation: { label: "En validation", color: "bg-amber-500", icon: Clock, verrouille: false },
  locked: { label: "Verrouillé (Niveau 1)", color: "bg-[var(--oh-accent-solid)]", icon: Lock, verrouille: true, peutDeverrouiller: true },
  devis_signe: { label: "Devis signé (Niveau 2)", color: "bg-emerald-500", icon: FileCheck, verrouille: true, peutDeverrouiller: true },
  dossier_depose: { label: "Dossier déposé", color: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", icon: Send, verrouille: true, peutDeverrouiller: true },
  paye: { label: "Payé (Final)", color: "bg-green-600", icon: Banknote, verrouille: true, peutDeverrouiller: false },
  envoye: { label: "Envoyé", color: "bg-[var(--oh-accent-solid)]", icon: Send, verrouille: false },
  signe: { label: "Signé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", icon: CheckCircle2, verrouille: true, peutDeverrouiller: true },
  archive: { label: "Archivé", color: "bg-[var(--oh-accent-solid)]", icon: FileText, verrouille: true, peutDeverrouiller: false },
  refuse: { label: "Refusé", color: "bg-red-500", icon: AlertTriangle, verrouille: false },
  annule: { label: "Annulé", color: "bg-[var(--oh-bg-elevated)]", icon: Trash2, verrouille: false },
};

const peutDeverrouiller = (statut) => ["locked", "devis_signe", "dossier_depose"].includes(statut);

// 🔑 FILTRE « TTC différent de l'aide » (demande JJ 16/10, grille devis OMV) — LECTURE SEULE. Les deux valeurs sont celles que la LISTE sert
// (`total_ttc`, `aides_deduites`, relues par `list_devis` comme `get_devis` : source unique, aucun recalcul ici), comparées AU CENTIME EXACT.
// Tri-état #557 : « oui » = écart, « non » = égal, « non renseigné » = devis SANS aide (aide absente ou nulle — défini explicitement).
export function etatEcartTtcAide(d) {
  const aide = d?.aides_deduites;
  if (aide === null || aide === undefined || Number(aide) === 0) return "sans_aide";
  const centimes = (x) => Math.round(Number(x || 0) * 100);
  return centimes(d?.total_ttc) !== centimes(aide) ? "ecart" : "egal";
}

export function matchEcartTtcAide(filtre, d) {
  if (!filtre || filtre === TRI_ETAT.TOUS) return true;
  const etat = etatEcartTtcAide(d);
  if (filtre === TRI_ETAT.OUI) return etat === "ecart";
  if (filtre === TRI_ETAT.NON) return etat === "egal";
  if (filtre === TRI_ETAT.INCONNU) return etat === "sans_aide";
  return true;
}

/** Le filtre n'est PROPOSÉ qu'au profil OMV (config `business_model` du tenant) — ⛔ pas de slug, ⛔ pas Climat (« installateur »). */
export const FILTRE_TTC_AIDE = { key: "filterTtcAide", label: "TTC différent de l'aide", type: "tribool", defaultValue: TRI_ETAT.TOUS };
export function filtresTtcAide() {
  return getBusinessModel() === "omv" ? [FILTRE_TTC_AIDE] : [];
}

export default function DevisFacturesPage({ tenantId, onNavigate }) {
  // 🔴 L'ONGLET FACTURES — ⛔ AUCUNE réorganisation de hub. La facture
  // appartient au domaine Finance dans le modèle, mais la navigation attend le
  // chantier produits/capacités. En attendant, elle vit LÀ OÙ ELLE NAÎT :
  // dans le parcours du devis qui la produit.
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterTtcAide, setFilterTtcAide] = useState(TRI_ETAT.TOUS);
  const [sendRow, setSendRow] = useState(null);  // devis OMV en cours d'envoi email
  // 🔴 N°1036 · LOT 2 — requête de la grille SERVEUR (profil ni TEF ni installateur : Mario). Pagination/tri/recherche/filtres en base.
  const REQUETE_INITIALE = { page: 1, pageSize: 25, tri: "created_at", ordre: "desc", q: "", client: "", site: "", etat: "all", dateMin: "", dateMax: "", archives: false };
  const [requete, setRequete] = useState(REQUETE_INITIALE);
  const [total, setTotal] = useState(0);
  const [filtreClient, setFiltreClient] = useState("");
  const [filtreSite, setFiltreSite] = useState("");
  const TRI_SERVEUR = { numero: "numero", client_nom: "client", objet: "objet", site: "site", date_devis: "date", total_ht: "total", statut: "etat", created_at: "created_at" };

  // 🔴 LE GESTE « FACTURER ». `FactureService.creer_depuis_devis` existait
  // depuis le 26/08 avec ZÉRO appelant : les 4 factures natives ont été
  // produites par script. Un service sans chemin utilisateur n'est pas livré.
  // ⚠️ L'éligibilité vient du SERVEUR (`POST /factures/eligibilite`), en UN
  // appel pour toute la grille. ⛔ On ne rejoue pas la règle ici : le seuil
  // « quel statut de devis se facture » a déjà DEUX vocabulaires en base, un
  // troisième dans le navigateur finirait par gagner en silence.
  const [eligibilites, setEligibilites] = useState({});
  const { items: statutsDevis } = useConfigList("statuts_devis");

  const isTEF = tenantId === "tef" || tenantId === "demo";
  // Profil installateur (OMV, Climat) — pilote les 3 options devis (Aperçu / PDF /
  // Modifier). Gating par PROFIL (business_model), pas par tenant. TEF (AMO) exclu.
  const isInstallateur = isInstallateurProfile();
  const serveurMode = !isTEF && !isInstallateur;
  // Filtres texte : on n'interroge le serveur qu'après 350 ms de pause (pas une requête par frappe).
  useEffect(() => {
    if (!serveurMode) return undefined;
    const t = setTimeout(() => setRequete((r) => (r.client === filtreClient && r.site === filtreSite) ? r : { ...r, client: filtreClient, site: filtreSite, page: 1 }), 350);
    return () => clearTimeout(t);
  }, [filtreClient, filtreSite, serveurMode]);

  // ─── Helpers format ───
  const formatPrice = (v) => v != null
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v)
    : "0,00 €";
  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

  // ─── Chargement (useCallback AVANT useEffect — anti-TDZ) ───
  const loadDevis = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("session_token");
      const headers = {
        ...enteteTenant(tenantId),
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      if (serveurMode) {
        const r = requete;
        const params = new URLSearchParams({ page: String(r.page), page_size: String(r.pageSize), tri: r.tri, ordre: r.ordre });
        if (r.q) params.set("q", r.q);
        if (r.client) params.set("client", r.client);
        if (r.site) params.set("site", r.site);
        if (r.etat && r.etat !== "all") params.set("etat", r.etat);
        if (r.dateMin) params.set("date_min", r.dateMin);
        if (r.dateMax) params.set("date_max", r.dateMax);
        if (r.archives) params.set("archives", "true");
        const rg = await fetch(`${API}/api/metier/devis/grille?${params.toString()}`, { headers, credentials: "include" });
        if (rg.ok) {
          const data = await rg.json();
          setDevis(data.items || []);
          setTotal(data.total || 0);
        } else {
          toast.error("Impossible de charger les devis");
        }
        return;
      }
      // T-176 — TOUTES les pages (le serveur en sert 50 par défaut) : le filtre « TTC ≠ aide » et les autres filtres côté navigateur
      // portent sur la liste complète du tenant, pas sur les 50 plus récents.
      const lu = await chargerTousLesDevis(async ({ limit, skip }) => {
        const res = await fetch(`${API}/api/metier/devis?limit=${limit}&skip=${skip}`, { headers, credentials: "include" });
        if (!res.ok) { console.error("Erreur API devis:", res.status); return null; }
        return res.json();
      });
      if (lu.items) {
        const list = isTEF
          ? lu.items.filter(d => d.type_document === "devis_amo" || d.type_document === "contrat_amo")
          : lu.items;
        // `id` stable pour OHGrid (déjà présent dans la data)
        setDevis(list);
      }
      if (lu.incomplet && lu.items) toast.error("Liste des devis incomplète : rechargez la page");
      if (lu.tronque) toast.warning("Liste des devis tronquée au plafond de chargement");
    } catch (error) {
      console.error("Erreur chargement devis:", error);
      toast.error("Erreur lors du chargement des devis");
    } finally {
      setLoading(false);
    }
  }, [tenantId, isTEF, serveurMode, requete]);

  useEffect(() => { loadDevis(); }, [loadDevis]);

  /** L'état de facturation de TOUTE la grille, en UN appel.
   *  ⛔ Pas une requête par ligne : 148 devis feraient 148 requêtes pour
   *  décider de l'affichage d'un bouton. */
  const loadEligibilites = useCallback(async (liste) => {
    const ids = (liste || []).map(d => d.id).filter(Boolean);
    if (!ids.length) { setEligibilites({}); return; }
    try {
      const res = await fetch(`${API}/api/metier/factures/eligibilite`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ devis_ids: ids }),
      });
      if (!res.ok) return;              // ⚠️ silencieux : sans état, pas de bouton
      const data = await res.json();
      setEligibilites(data.etats || {});
    } catch { /* l'absence d'état masque le bouton — ⛔ jamais un faux « éligible » */ }
  }, []);

  useEffect(() => { loadEligibilites(devis); }, [devis, loadEligibilites]);

  /** 🔴 LE GESTE. Devis éligible → facture BROUILLON, sans ressaisie, puis on
   *  OUVRE la facture créée. ⛔ Aucun refus muet : le motif du serveur s'affiche. */
  const { isFeatureEnabled } = usePermissions();
  const facturationActive = isFeatureEnabled("commercial.factures");

  const handleFacturer = useCallback(async (row) => {
    const etat = eligibilites[row.id];
    if (etat && !etat.eligible) { toast.error(etat.motif); return; }
    try {
      const res = await fetch(`${API}/api/metier/factures/depuis-devis`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ devis_id: row.id }),
      });
      const data = await res.json();
      if (!res.ok) {
        // 🔑 LE DOUBLE CLIC ARRIVE ICI : la 2ᵉ requête reçoit 409 et le motif
        // nomme la facture déjà créée. ⛔ Pas « erreur », pas un silence.
        toast.error(data.detail || `Facturation refusée (${res.status})`);
        loadEligibilites(devis);
        return;
      }
      if (data.mentions_manquantes?.length) {
        toast.warning(`Facture créée — mentions à compléter avant émission : `
                      + data.mentions_manquantes.join(", "));
      } else {
        toast.success(data.message || "Facture brouillon créée");
      }
      // 🔑 ON AMÈNE L'UTILISATEUR À SA FACTURE, dans l'entrée de menu native.
      // ⛔ Pas « elle est quelque part dans la liste ».
      onNavigate?.("factures", { factureId: data.id });
      loadEligibilites(devis);
    } catch (e) {
      toast.error("Facturation impossible : " + e.message);
    }
  }, [eligibilites, devis, loadEligibilites, onNavigate]);

  const handleValider = useCallback(async (row) => {
    try {
      await emettreDevis(row.id);
      toast.success("Devis validé");
      loadDevis();
    } catch (e) {
      toast.error(e.message || "Validation refusée");
    }
  }, [loadDevis]);

  const handleVoirFacture = useCallback((row) => {
    const etat = eligibilites[row.id];
    if (!etat?.facture_id) return;
    onNavigate?.("factures", { factureId: etat.facture_id });
  }, [eligibilites, onNavigate]);

  // ─── Filtrage selon filterType/filterStatut/showArchived ───
  const visibleDevis = useMemo(() => serveurMode ? devis : devis.filter(d => {
    if (!showArchived && d.statut === "archive") return false;
    // OMV : filtre sur la clé WORKFLOW (devisStatutKey) — les options reflètent les
    // vrais statuts OMV. TEF : filtre sur le statut brut de l'enum backend.
    if (filterStatut !== "all") {
      const matches = isInstallateur ? devisStatutKey(d) === filterStatut : d.statut === filterStatut;
      if (!matches) return false;
    }
    if (filterType === "brouillons" && !(d.statut === "draft" || d.statut === "validation")) return false;
    if (filterType === "valides" && !(d.statut === "locked" || d.statut === "signe")) return false;
    if (!matchEcartTtcAide(filterTtcAide, d)) return false;
    return true;
  }), [devis, showArchived, filterStatut, filterType, filterTtcAide, isInstallateur, serveurMode]);

  // ─── Actions unitaires ───
  const handleContinue = useCallback((row) => {
    if (isTEF || !onNavigate) return;
    window.__devisId = row.id;
    window.__dossierId = row.dossier_id;
    onNavigate("devis-wizard", { devisId: row.id, dossierId: row.dossier_id });
  }, [isTEF, onNavigate]);

  // Ouvrir un devis = VUE DOCUMENT (DevisDocument), PAS le wizard.
  // Le wizard reste réservé à l'action "Continuer / Modifier" (handleContinue).
  const handleView = useCallback((row) => {
    if (isTEF || !onNavigate) return;
    window.__devisId = row.id;
    window.__dossierId = row.dossier_id;
    onNavigate("devis-document", { devisId: row.id, dossierId: row.dossier_id, from: "devis-factures" });
  }, [isTEF, onNavigate]);

  // OMV : envoi/renvoi du devis par email (même modal que la fiche/aperçu).
  const handleSendEmail = useCallback((row) => setSendRow(row), []);

  const handleDownloadAmoPdf = useCallback(async (row) => {
    try {
      if (!row.pdf_url) { toast.error("Aucun PDF disponible pour ce devis"); return; }
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}${row.pdf_url}`, {
        headers: {
          ...enteteTenant(tenantId),
          ...(token && { Authorization: `Bearer ${token}` }),
        }, credentials: "include",
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${row.type_document || "devis"}_${row.numero || row.id}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else toast.error("Erreur lors du téléchargement du PDF");
    } catch (err) { console.error(err); toast.error("Erreur réseau"); }
  }, [tenantId]);

  // Installateur (OMV/Climat) — PDF d'aperçu : même endpoint que DevisDocument
  // (POST /documents/devis/{id}/pdf/preview → pdf_base64). Les devis installateur
  // n'ont pas de pdf_url AMO ; on télécharge donc le rendu d'aperçu.
  const handleDownloadPreview = useCallback(async (row) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/documents/devis/${row.id}/pdf/preview`, {
        method: "POST",
        headers: {
          ...enteteTenant(tenantId || localStorage.getItem("current_tenant_slug")),
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        credentials: "include",
      });
      if (!res.ok) { toast.error("Aperçu PDF indisponible"); return; }
      const d = await res.json();
      if (!d.pdf_base64) { toast.error("Aperçu PDF indisponible"); return; }
      const a = document.createElement("a");
      a.href = `data:application/pdf;base64,${d.pdf_base64}`;
      a.download = `Devis_${String(row.numero || row.id).replace(/\//g, "-")}.pdf`;
      a.click();
    } catch (err) { console.error(err); toast.error("Erreur réseau pendant le téléchargement du PDF"); }
  }, [tenantId]);

  const handleDelete = useCallback(async (row) => {
    if (!window.confirm("Supprimer ce brouillon ?")) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/devis/${row.id}`, {
        method: "DELETE",
        headers: {
          // 🔴 N°774 — ⛔ plus de repli "climatpac" : sans tenant, on n'en
          // invente pas un ; le serveur prend celui de la session.
          ...(tenantId && { ...enteteTenant(tenantId) }),
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (res.ok) { toast.success("Brouillon supprimé"); loadDevis(); }
      else {
        // 🔴 N°774 — le MOTIF du serveur, en clair (comme l'onglet du dossier,
        // N°690-1) : « Erreur » seul ⛔ ne dit pas quoi faire.
        const data = await res.json().catch(() => ({}));
        toast.error(typeof data.detail === "string" ? data.detail
          : `Suppression refusée (erreur ${res.status})`);
      }
    } catch (err) { console.error(err); toast.error("Erreur réseau"); }
  }, [tenantId, loadDevis]);

  const handleArchive = useCallback(async (row) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/devis/${row.id}/archiver`, {
        method: "POST",
        headers: {
          ...enteteTenant(tenantId),
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        }, credentials: "include",
      });
      if (res.ok) { toast.success("Devis archivé"); loadDevis(); }
      else toast.error("Erreur archivage");
    } catch { toast.error("Erreur réseau"); }
  }, [tenantId, loadDevis]);

  const handleUnarchive = useCallback(async (row) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/devis/${row.id}/desarchiver`, {
        method: "POST",
        headers: {
          ...enteteTenant(tenantId),
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        }, credentials: "include",
      });
      if (res.ok) { toast.success("Devis désarchivé"); loadDevis(); }
      else toast.error("Erreur désarchivage");
    } catch { toast.error("Erreur réseau"); }
  }, [tenantId, loadDevis]);

  const handleDeverrouiller = useCallback(async (row) => {
    if (!peutDeverrouiller(row.statut)) { toast.error("Ce devis ne peut pas être déverrouillé"); return; }
    if (row.statut === "paye") { toast.error("Un devis payé ne peut jamais être déverrouillé"); return; }
    const motif = window.prompt("⚠️ DÉVERROUILLAGE ADMIN\n\nMotif (min 10 caractères):");
    if (!motif || motif.length < 10) { toast.error("Motif obligatoire (10 car. min)"); return; }
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/devis/${row.id}/deverrouiller`, {
        method: "POST",
        headers: {
          ...enteteTenant(tenantId),
          "Content-Type": "application/json",
          "X-Admin-Action": "true",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ motif, commentaire: "Déverrouillage depuis interface" }),
      });
      const data = await res.json();
      if (res.ok && data.success) { toast.success(`Déverrouillé: ${data.statut_avant} → ${data.statut_apres}`); loadDevis(); }
      else toast.error(data.detail || "Erreur déverrouillage");
    } catch { toast.error("Erreur réseau"); }
  }, [tenantId, loadDevis]);

  // ─── Bulk actions ───
  // body (optionnel) : corps JSON transmis à CHAQUE appel (ex. {template_key} pour
  // l'envoi de devis). Sans body → comportement historique (archiver/supprimer…).
  const bulkRun = useCallback(async (ids, url, method = "POST", body = null) => {
    const token = localStorage.getItem("session_token");
    let ok = 0, errs = 0;
    for (const id of ids) {
      try {
        const res = await fetch(`${API}${url.replace(":id", id)}`, {
          method,
          headers: {
            ...enteteTenant(tenantId),
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          ...(body ? { body: JSON.stringify(body) } : {}),
          credentials: "include",
        });
        if (res.ok) ok++; else errs++;
      } catch { errs++; }
    }
    return { ok, errs };
  }, [tenantId]);

  const handleBulkArchive = useCallback(async (ids) => {
    if (!window.confirm(`Archiver ${ids.length} devis ?`)) return;
    const { ok, errs } = await bulkRun(ids, "/api/metier/devis/:id/archiver");
    if (ok) toast.success(`${ok} devis archivé(s)`);
    if (errs) toast.error(`${errs} erreur(s)`);
    loadDevis();
  }, [bulkRun, loadDevis]);

  const handleBulkUnarchive = useCallback(async (ids) => {
    if (!window.confirm(`Désarchiver ${ids.length} devis ?`)) return;
    const { ok, errs } = await bulkRun(ids, "/api/metier/devis/:id/desarchiver");
    if (ok) toast.success(`${ok} devis désarchivé(s)`);
    if (errs) toast.error(`${errs} erreur(s)`);
    loadDevis();
  }, [bulkRun, loadDevis]);

  const handleBulkDelete = useCallback(async (ids) => {
    const selectedDevis = devis.filter(d => ids.includes(d.id));
    const nonSupprimables = selectedDevis.filter(d => !["draft", "archive", "annule"].includes(d.statut));
    if (nonSupprimables.length > 0) {
      toast.error(`${nonSupprimables.length} devis ne peuvent pas être supprimés (seuls brouillons/archivés/annulés)`);
      return;
    }
    if (!window.confirm(`Supprimer définitivement ${ids.length} devis ?`)) return;
    const { ok, errs } = await bulkRun(ids, "/api/metier/devis/:id", "DELETE");
    if (ok) toast.success(`${ok} devis supprimé(s)`);
    if (errs) toast.error(`${errs} erreur(s)`);
    loadDevis();
  }, [devis, bulkRun, loadDevis]);

  // ─── Bulk envoi / renvoi devis OMV (CC-DEV1-21) ───
  // Éligibilité (selon le marqueur email_envoi, source unique de l'état d'envoi) :
  //   « Envoyer »  → devis PAS encore envoyé : statut "emis" ET email_envoi.status ≠ "sent".
  //   « Renvoyer » → devis DÉJÀ envoyé : email_envoi.status === "sent".
  const _isSent = (d) => d?.email_envoi?.status === "sent";
  const _isEnvoyable = (d) => d?.statut === "emis" && !_isSent(d);
  const _isRenvoyable = (d) => _isSent(d);

  // template_key UNIQUE pour le lot : MÊME source que SendDevisOmvModal (templates
  // "devis" du tenant, Design Studio). Priorité au template envoi_devis (corrigé
  // CC-SAV-04/CC-DEV1-20), sinon le 1er template devis du tenant.
  const resolveDevisTemplateKey = useCallback(async () => {
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/api/governance/email-config/templates?owner=design_studio&category=devis`, {
        headers: {
          ...enteteTenant(tenantId),
          ...(token && { Authorization: `Bearer ${token}` }),
        }, credentials: "include",
      });
      const data = await res.json();
      const list = (data?.templates || []).filter((t) => t.active !== false);
      const tpl = list.find((t) => /envoi_devis/i.test(t.key || "")) || list[0];
      return tpl?.key || null;
    } catch { return null; }
  }, [tenantId]);

  const handleBulkSend = useCallback(async (ids, mode) => {
    // mode: "send" (Envoyer) | "resend" (Renvoyer)
    const selected = devis.filter((d) => ids.includes(d.id));
    const eligible = selected.filter(mode === "send" ? _isEnvoyable : _isRenvoyable);
    const ignored = selected.length - eligible.length;
    const verb = mode === "send" ? "Envoyer" : "Renvoyer";
    if (!eligible.length) {
      toast.info(`Aucun devis éligible à « ${verb} » — ${ignored} ignoré(s) (déjà envoyé ou non éligible)`);
      return;
    }
    const templateKey = await resolveDevisTemplateKey();
    if (!templateKey) { toast.error("Aucun template d'envoi de devis (Design Studio → Emails Métier)"); return; }
    if (!window.confirm(`${verb} ${eligible.length} devis ?${ignored ? ` (${ignored} ignoré[s] : déjà envoyé ou non éligible)` : ""}`)) return;
    const { ok, errs } = await bulkRun(eligible.map((d) => d.id), "/api/metier/devis/:id/send-email", "POST", { template_key: templateKey });
    const done = mode === "send" ? "envoyé(s)" : "renvoyé(s)";
    toast.success(`${ok} ${done} · ${ignored} ignoré(s)${errs ? ` · ${errs} erreur(s)` : ""}`);
    loadDevis();
  }, [devis, bulkRun, resolveDevisTemplateKey, loadDevis]);

  // ─── Config OHGrid ───
  const stats = useMemo(() => {
    if (serveurMode) return [];       // ⛔ un KPI calculé sur UNE page serait faux : le total vient du serveur (compteur de la grille)
    const all = visibleDevis;
    // OMV : KPIs PAR STATUT (workflow Brouillon→Payé) + Total — pour comparer aux
    // dossiers et comprendre les écarts. Compte via la clé workflow (devisStatutKey).
    if (isInstallateur) {
      const order = (statutsDevis && statutsDevis.length ? statutsDevis : DEVIS_STATUT_DEFAULTS);
      const palette = { brouillon: "gray", valide: "blue", envoye: "blue", signe: "green",
        en_cours: "amber", a_facturer: "purple", facture: "purple", paye: "green" };
      const cards = order.map((it) => {
        const id = it.id || it.value;
        return {
          key: `st_${id}`, label: it.label || id,
          value: all.filter((d) => devisStatutKey(d) === id).length,
          color: palette[id] || "gray",
          filter: (row) => devisStatutKey(row) === id,
        };
      });
      // Archivés : comptés sur la liste BRUTE (devis) — incluse même quand le toggle
      // "Inclure archivés" est off (la liste backend ne filtre pas archive).
      cards.push({ key: "archive", label: "Archivés",
        value: devis.filter((d) => d.statut === "archive").length,
        icon: Archive, color: "amber", filter: (row) => row.statut === "archive" });
      cards.push({ key: "total", label: "Total", value: all.length, icon: Receipt, color: "blue", filter: null });
      return cards;
    }
    const totalCASigne = all.filter(d => d.statut === "signe").reduce((s, d) => s + (d.total_ttc || 0), 0);
    return [
      { key: "brouillons", label: "Brouillons", value: all.filter(d => d.statut === "draft").length,
        icon: Edit, color: "gray", filter: (row) => row.statut === "draft" },
      { key: "verrouilles", label: "Verrouillés", value: all.filter(d => d.statut === "locked").length,
        icon: Lock, color: "blue", filter: (row) => row.statut === "locked" },
      { key: "signes", label: "Signés", value: all.filter(d => d.statut === "signe").length,
        icon: CheckCircle2, color: "green", filter: (row) => row.statut === "signe" },
      { key: "ca", label: "CA signés", value: formatPrice(totalCASigne), icon: Euro, color: "purple", filter: null },
    ];
  }, [visibleDevis, devis, isInstallateur, statutsDevis, serveurMode]);

  // Options du filtre statut : OMV = vrais statuts workflow (liste éditable
  // statuts_devis, repli DEVIS_STATUT_DEFAULTS) ; TEF = statuts de l'enum backend.
  // Fin du hardcode TEF (draft/validation/locked/signe/refuse/annule) sur OMV.
  const statutFilterOptions = useMemo(() => {
    if (isInstallateur) {
      const order = (statutsDevis && statutsDevis.length ? statutsDevis : DEVIS_STATUT_DEFAULTS);
      return order.map((it) => ({ value: it.id || it.value, label: it.label || it.id }));
    }
    return [
      { value: "draft", label: "Brouillon" },
      { value: "validation", label: "En validation" },
      { value: "locked", label: "Verrouillé" },
      { value: "signe", label: "Signé" },
      { value: "refuse", label: "Refusé" },
      { value: "annule", label: "Annulé" },
    ];
  }, [isInstallateur, statutsDevis]);

  const advancedFilters = useMemo(() => serveurMode ? [
    { key: "etat", label: "État", type: "select", defaultValue: "all",
      options: [
        { value: "draft", label: "Brouillon" }, { value: "emis", label: "Validé" }, { value: "signe,devis_signe", label: "Signé" },
        { value: "refuse", label: "Refusé" }, { value: "annule", label: "Annulé" },
      ] },
    { key: "archives", label: "Inclure archivés", type: "checkbox", defaultValue: false },
  ] : [
    { key: "filterType", label: "Type", type: "select", defaultValue: "all",
      options: [
        { value: "brouillons", label: "Brouillons uniquement" },
        { value: "valides", label: "Validés uniquement" },
      ] },
    { key: "filterStatut", label: "Statut", type: "select", defaultValue: "all",
      options: statutFilterOptions },
    { key: "showArchived", label: "Inclure archivés", type: "checkbox", defaultValue: false },
    ...filtresTtcAide(),
  ], [statutFilterOptions, serveurMode]);

  const handleAdvFilter = useCallback(({ key, value }) => {
    if (serveurMode) {
      if (key === "etat") setRequete((r) => ({ ...r, etat: value, page: 1 }));
      if (key === "archives") setRequete((r) => ({ ...r, archives: !!value, page: 1 }));
      return;
    }
    if (key === "filterType") setFilterType(value);
    if (key === "filterStatut") setFilterStatut(value);
    if (key === "showArchived") setShowArchived(value);
    if (key === FILTRE_TTC_AIDE.key) setFilterTtcAide(value);
  }, [serveurMode]);

  const searchFilter = useCallback((row, q) => (
    (row.numero || "").toLowerCase().includes(q) ||
    (row.numero_provisoire || "").toLowerCase().includes(q) ||
    (row.objet || "").toLowerCase().includes(q) ||
    (row.client_nom || "").toLowerCase().includes(q)
  ), []);

  // 🔴 N°1036 · LOT 2 — colonnes de travail de la grille serveur : N° devis | Client | Titre/Objet | Chantier/Site | Date | Total HT | État.
  const TON_ETAT = { draft: "gray", brouillon: "gray", emis: "blue", signe: "green", devis_signe: "green", paye: "green",
                     refuse: "red", annule: "red", validation: "amber", locked: "blue" };
  const colonnesServeur = useMemo(() => [
    { key: "numero", label: "N° devis",
      render: (v, row) => (
        <span className="font-medium text-[var(--oh-text)]" data-testid={`devis-numero-${row.id}`}>
          {v ? (devisPrefixEnabled() ? withDevisPrefix(v) : v) : `BROUILLON-${row.id?.slice(-6)}`}
        </span>) },
    { key: "client_nom", label: "Client",
      render: (v) => <span className="text-sm text-[var(--oh-text)]">{v || "Client non défini"}</span> },
    { key: "objet", label: "Titre / Objet",
      render: (v) => <span className="text-sm text-[var(--oh-text-secondary)] truncate max-w-xs block">{v || "Sans objet"}</span> },
    { key: "site", label: "Chantier / Site",
      render: (v, row) => (
        <div>
          <p className="text-sm text-[var(--oh-text)]">{v || "Adresse non renseignée"}</p>
          {row.dossier_reference && <p className="text-xs text-[var(--oh-text-muted)]">{row.dossier_reference}</p>}
        </div>) },
    { key: "date_devis", label: "Date", render: (v) => <span className="text-sm text-[var(--oh-text-secondary)]">{formatDate(v)}</span> },
    { key: "total_ht", label: "Total HT",
      render: (v) => <span className="font-medium text-[var(--oh-text)]">{formatPrice(v)}</span> },
    { key: "statut", label: "État",
      render: (v, row) => {
        const ton = TON_ETAT[v] || "amber";
        return (
          <div className="space-y-1">
            <span data-testid={`devis-etat-${row.id}`} className="text-xs rounded-lg px-2 py-0.5 inline-block"
                  style={{ background: `var(--oh-stat-${ton}-bg)`, color: `var(--oh-stat-${ton}-text)`, border: `0.5px solid var(--oh-stat-${ton}-border)` }}>
              {row.statut_libelle || v}
            </span>
            {row.facture?.numero && <p className="text-xs text-[var(--oh-text-muted)]" data-testid={`devis-facture-${row.id}`}>Facture {row.facture.numero}</p>}
          </div>);
      } },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo(() => [
    {
      key: "numero", label: "Numéro / Objet",
      render: (v, row) => {
        const num = row.numero || row.numero_provisoire;
        // Préfixe « Devis - » VISUEL pour OMV (valeur stockée nue, recherche/tri intacts).
        const label = num
          ? (devisPrefixEnabled() ? withDevisPrefix(num) : num)
          : (isTEF ? (row.type_document === "contrat_amo" ? "Contrat AMO" : "Devis AMO") : `BROUILLON-${row.id?.slice(-6)}`);
        return (
        <div>
          <p className="font-medium text-[var(--oh-text)]">{label}</p>
          <p className="text-xs text-[var(--oh-text-secondary)] truncate max-w-xs">
            {isTEF && row.type_document && (
              <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border border-[var(--oh-info-border)] text-xs mr-1">
                {row.type_document === "contrat_amo" ? "Contrat AMO" : "Devis AMO"}
              </Badge>
            )}
            {row.objet || (isTEF ? "" : "Sans objet")}
          </p>
          {/* Trace dernier envoi email (criterion : visible aussi dans la grille). */}
          {row.email_envoi && <DevisEnvoiTrace envoi={row.email_envoi} />}
        </div>
        );
      },
    },
    {
      key: "dossier_numero", label: "Dossier", sortable: false,
      render: (_, row) => (row.dossier_numero || row.dossier_id) ? (
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[var(--oh-text-secondary)]" />
          <span className="text-sm text-[var(--oh-text-muted)]">
            {row.dossier_numero || `#${row.dossier_id?.slice(-6) || "?"}`}
          </span>
          {row.is_principal && <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] text-xs">★</Badge>}
        </div>
      ) : <span className="text-xs text-[var(--oh-text-secondary)]">—</span>,
    },
    {
      key: "client_nom", label: "Client",
      render: (v) => (
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-[var(--oh-text-secondary)]" />
          <span className="text-sm text-[var(--oh-text-secondary)]">{v || "Client non défini"}</span>
        </div>
      ),
    },
    // D7 — CUMAC AVANT Montant TTC (ordre demandé JJ). Gaté installateur.
    // CUMAC = volume_kwh_cumac (joint depuis le dossier parent, cf. backend).
    ...(isInstallateur ? [
      {
        key: "volume_kwh_cumac", label: "CUMAC",
        render: (v) => <span className="text-sm text-[var(--oh-text-secondary)]">{v != null ? `${Number(v).toLocaleString("fr-FR")} kWh` : "—"}</span>,
      },
    ] : []),
    {
      key: "total_ttc", label: "Montant TTC",
      render: (v) => <span className="font-medium text-[var(--oh-success-text)]">{formatPrice(v)}</span>,
    },
    // Prime CEE (= aides_deduites) après Montant TTC. Gaté installateur.
    ...(isInstallateur ? [
      {
        key: "montant_prime_cee", label: "Prime CEE",
        render: (v) => <span className="font-medium text-cyan-400">{v ? formatPrice(v) : "—"}</span>,
      },
    ] : []),
    {
      key: "statut", label: "Statut",
      render: (v, row) => {
        const cfg = STATUT_CONFIG[v] || STATUT_CONFIG.draft;
        const Icon = cfg.icon;
        return (
          <Badge className={`${cfg.color} text-[var(--oh-text)] flex items-center gap-1 w-fit`}>
            <Icon className="w-3 h-3" /> {devisStatutLabel(row, statutsDevis, cfg.label)}
          </Badge>
        );
      },
    },
    {
      key: "updated_at", label: "Date",
      render: (v, row) => (
        <div className="flex items-center gap-1 text-xs text-[var(--oh-text-secondary)]">
          <Calendar className="w-3 h-3" /> {formatDate(v || row.created_at)}
        </div>
      ),
    },
  ], [isTEF, isInstallateur]);

  const rowActions = useMemo(() => [
    { label: "Télécharger PDF", icon: Download,
      onClick: handleDownloadAmoPdf,
      show: () => isTEF, className: "text-[var(--oh-info-text)]" },
    // Installateur (OMV/Climat) : 3 options PDF / Aperçu / Modifier.
    // Aperçu (= vue document) désormais visible AUSSI sur les brouillons.
    // 🔴 N°1031 — mêmes mots que la fiche devis et le dossier : Voir / Télécharger / Modifier, pour TOUT profil non-TEF
    //    (Mario n'avait aucune de ces actions : elles étaient réservées au profil installateur).
    { label: "Voir", icon: Eye,
      onClick: handleView,
      show: (row) => !isTEF },
    { label: "Télécharger", icon: Download,
      onClick: handleDownloadPreview,
      show: (row) => !isTEF, className: "text-[var(--oh-info-text)]" },
    { label: "Envoyer / Renvoyer par email", icon: Send,
      onClick: handleSendEmail,
      show: (row) => isInstallateur && row.statut === "emis",
      className: "text-[var(--oh-accent-text)]" },
    // 🔴 FACTURER — le geste qui manquait. Visible sur un devis ÉLIGIBLE, et
    // l'éligibilité est celle du SERVEUR. ⛔ Pas de seuil recopié ici.
    // 🔴 VERROU — la conversion Devis→Facture ⛔ n'est PAS qualifiée partout.
    // La CAPACITÉ DÉCLARÉE `commercial.factures` commande, ⛔ pas un slug.
    // ⚠️ `isFeatureEnabled` rend `false` TANT QUE le contexte charge (DEC-079 :
    // unknown ≠ enabled) ⇒ l'action ⛔ n'apparaît JAMAIS par intermittence.
    { label: "Créer une facture", icon: Euro,
      onClick: handleFacturer,
      show: (row) => facturationActive && eligibilites[row.id]?.eligible === true,
      className: "text-[var(--oh-success-text)]" },
    // ⚠️ Le devis DÉJÀ facturé ne perd pas son chemin : il mène à sa facture.
    // ⛔ Un bouton qui disparaît sans rien dire laisse croire à une perte.
    { label: "Voir la facture", icon: Receipt,
      onClick: handleVoirFacture,
      show: (row) => Boolean(eligibilites[row.id]?.facture_id),
      className: "text-[var(--oh-accent-text)]" },
    { label: "Modifier", icon: Edit,
      onClick: handleContinue,
      // 🔴 N°1036 · cohérence des écrans : UNE règle décide de « Modifier » — `estModifiableDevis`, la MÊME que le dossier et la fiche devis
      //    (un devis validé « emis » reste modifiable tant qu'il n'est pas signé). Profil installateur (OMV/Climat) : règle historique inchangée.
      show: (row) => !isTEF && (serveurMode ? estModifiableDevis(row.statut) : ["draft", "validation"].includes(row.statut)),
      className: "text-[var(--oh-success-text)]" },
    // 🔴 N°1031 — « Valider le devis » = l'émission NATIVE existante (comme la fiche devis et le dossier). Hors profil
    //    installateur (OMV/Climat : statut piloté par leur sélecteur).
    { label: "Valider le devis", icon: CheckCircle2,
      onClick: handleValider,
      show: (row) => !isTEF && !isInstallateur && row.statut === "draft",
      className: "text-[var(--oh-accent-text)]" },
    { label: "Archiver", icon: Archive,
      onClick: handleArchive,
      show: (row) => row.statut !== "archive" && row.statut !== "paye",
      className: "text-[var(--oh-info-text)]" },
    { label: "Désarchiver", icon: ArchiveRestore,
      onClick: handleUnarchive,
      show: (row) => row.statut === "archive",
      className: "text-[var(--oh-accent-text)]" },
    { label: "Déverrouiller (admin)", icon: Unlock,
      onClick: handleDeverrouiller,
      show: (row) => !isTEF && peutDeverrouiller(row.statut),
      className: "text-[var(--oh-info-text)]" },
    { label: "Supprimer", icon: Trash2,
      onClick: handleDelete,
      show: (row) => row.statut === "draft" || row.statut === "archive" || row.statut === "annule",
      className: "text-[var(--oh-error-text)]" },
  ], [isTEF, isInstallateur, serveurMode, facturationActive, eligibilites, handleFacturer, handleValider, handleVoirFacture, handleDownloadAmoPdf, handleDownloadPreview, handleContinue, handleView, handleSendEmail, handleArchive, handleUnarchive, handleDeverrouiller, handleDelete]);

  const bulkActions = useMemo(() => [
    // Envoyer / Renvoyer : OMV/Climat (profil installateur) — TEF gère ses devis AMO autrement.
    ...(isInstallateur ? [
      { label: "Envoyer", icon: Send, onClick: (ids) => handleBulkSend(ids, "send"),
        className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]" },
      { label: "Renvoyer", icon: RefreshCw, onClick: (ids) => handleBulkSend(ids, "resend"),
        className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]" },
    ] : []),
    { label: "Archiver", icon: Archive, onClick: handleBulkArchive,
      className: "border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]" },
    { label: "Désarchiver", icon: ArchiveRestore, onClick: handleBulkUnarchive,
      className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]" },
    { label: "Supprimer", icon: Trash2, onClick: handleBulkDelete,
      className: "border-[var(--oh-error-border)] text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)]" },
  ], [isInstallateur, handleBulkSend, handleBulkArchive, handleBulkUnarchive, handleBulkDelete]);

  return (
    <div className="space-y-6 p-6" data-testid="devis-factures-page">
      {/* 🔴 L'ONGLET « Factures » A ÉTÉ RETIRÉ D'ICI. Factures est désormais une
          ENTRÉE DE MENU native (décision CTO du 02/09) : le garder aussi en
          onglet aurait fait DEUX chemins vers le MÊME écran — exactement la
          duplication qu'on reproche ailleurs. Le geste « Facturer » navigue
          vers cette entrée, avec la facture ouverte. */}
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[var(--oh-text)] flex items-center gap-2">
            <Receipt className="w-7 h-7 text-[var(--oh-success-text)]" />
            {isTEF ? "Devis AMO & Contrats AMO" : "Devis & Factures"}
          </h1>
          <p className="text-[var(--oh-text-secondary)]">
            {isTEF ? "Les devis AMO sont générés depuis la fiche dossier" : "Gérez vos devis et factures"}
          </p>
        </div>
        <Button variant="outline" onClick={loadDevis} className="border-[var(--oh-border)]" data-testid="devis-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      </div>

      {serveurMode && (
        <div className="flex flex-wrap items-end gap-3" data-testid="devis-filtres-serveur">
          <label className="text-xs text-[var(--oh-text-secondary)] flex flex-col gap-1">Client
            <input data-testid="devis-filtre-client" aria-label="Filtrer par client" value={filtreClient}
                   onChange={(e) => setFiltreClient(e.target.value)} placeholder="Ex. CABINET EXEMPLE"
                   className="h-9 rounded-lg px-3 text-sm bg-[var(--oh-bg-surface)] text-[var(--oh-text)] border border-[var(--oh-border)]" />
          </label>
          <label className="text-xs text-[var(--oh-text-secondary)] flex flex-col gap-1">Chantier / site
            <input data-testid="devis-filtre-site" aria-label="Filtrer par chantier ou site" value={filtreSite}
                   onChange={(e) => setFiltreSite(e.target.value)} placeholder="Adresse ou ville"
                   className="h-9 rounded-lg px-3 text-sm bg-[var(--oh-bg-surface)] text-[var(--oh-text)] border border-[var(--oh-border)]" />
          </label>
          <label className="text-xs text-[var(--oh-text-secondary)] flex flex-col gap-1">Du
            <ChampDate  data-testid="devis-filtre-date-min" aria-label="Date du devis à partir du" value={requete.dateMin}
                   onChange={(e) => setRequete((r) => ({ ...r, dateMin: e.target.value, page: 1 }))}
                   className="h-9 rounded-lg px-3 text-sm bg-[var(--oh-bg-surface)] text-[var(--oh-text)] border border-[var(--oh-border)]" />
          </label>
          <label className="text-xs text-[var(--oh-text-secondary)] flex flex-col gap-1">Au
            <ChampDate  data-testid="devis-filtre-date-max" aria-label="Date du devis jusqu'au" value={requete.dateMax}
                   onChange={(e) => setRequete((r) => ({ ...r, dateMax: e.target.value, page: 1 }))}
                   className="h-9 rounded-lg px-3 text-sm bg-[var(--oh-bg-surface)] text-[var(--oh-text)] border border-[var(--oh-border)]" />
          </label>
        </div>
      )}

      <OHGrid
        data={visibleDevis}
        columns={serveurMode ? colonnesServeur : columns}
        {...(serveurMode ? {
          serverSide: true, totalCount: total, pageSize: requete.pageSize,
          onPageChange: ({ page, pageSize }) => setRequete((r) => ({ ...r, page, pageSize })),
          onSortChange: ({ key, dir }) => setRequete((r) => ({ ...r, tri: TRI_SERVEUR[key] || "created_at", ordre: dir, page: 1 })),
          onSearchChange: (q) => setRequete((r) => ({ ...r, q, page: 1 })),
        } : {})}
        stats={stats}
        advancedFilters={advancedFilters}
        onAdvancedFilter={handleAdvFilter}
        rowActions={rowActions}
        bulkActions={bulkActions}
        selectable={true}
        pagination={true}
        pageSize={25}
        loading={loading}
        onRefresh={loadDevis}
        onRowClick={(row) => {
          // Clic sur une ligne = ouvrir la VUE DOCUMENT (jamais le wizard).
          if (isTEF) handleDownloadAmoPdf(row);
          else handleView(row);
        }}
        searchPlaceholder={serveurMode ? "Rechercher : numéro, objet, client, adresse…" : "Rechercher par numéro, objet ou client..."}
        searchFilter={searchFilter}
        defaultSortKey={serveurMode ? "created_at" : "updated_at"}
        defaultSortDir="desc"
        emptyMessage="Aucun devis trouvé"
        testIdPrefix="devis"
      />

      <SendDevisOmvModal
        open={!!sendRow}
        onClose={() => setSendRow(null)}
        devis={sendRow}
        onSent={() => loadDevis()}
      />
      </>
    </div>
  );
}
